import {
	CollectionModelField,
	CollectionModelFieldAnchor,
	CollectionModelFieldHeight,
	CollectionModelFieldImage,
	CollectionModelFieldLocation,
	CollectionModelFieldNumber,
	CollectionModelFieldSelectList,
	CollectionModelFieldSubForm,
	CollectionModelFieldTrueSize,
	CollectionModelFieldVector,
	CollectionModelFieldVerticalSeparation,
	CollectionModelFieldYesNo,
} from '@ikegps/core-shared';
import { IkeUnit } from '@ikegps/core-shared';
import { T, Translate } from '../../i18n/index';
import { Services } from '../../services';

const LOCATION_PRECISION = 5;

export interface PrintableReportItem {
	title: string;
	value: string | null;
}

/**
 * The DataExtractor class provides static methods for extracting printable values from CollectionModelField objects.
 */
export class DataExtractor {
	/**
	 * Creates a printable report item from a CollectionModelField.
	 * @param field - The CollectionModelField object.
	 * @returns The PrintableReportItem object.
	 */
	public static createPrintableValue(field: CollectionModelField): PrintableReportItem {
		const printable = { title: field.schema.displayName, value: null };

		if (field.hasValue()) {
			printable.value = this.extract(field);
		}

		return printable;
	}

	private static extract(field: CollectionModelField): string | null {
		switch (true) {
			case field instanceof CollectionModelFieldSubForm:
			case field instanceof CollectionModelFieldImage:
			case field instanceof CollectionModelFieldTrueSize:
				return null;
			case field instanceof CollectionModelFieldLocation:
				return this.extractLocation(field);
			case field instanceof CollectionModelFieldVector:
				return this.extractVector(field);
			case field instanceof CollectionModelFieldAnchor:
				return this.extractAnchor(field);
			case field instanceof CollectionModelFieldHeight:
				return this.extractHeight(field);
			case field instanceof CollectionModelFieldNumber:
				return this.extractNumber(field);
			case field instanceof CollectionModelFieldYesNo:
				return this.extractYesNo(field);
			case field instanceof CollectionModelFieldSelectList:
				return this.extractSelectList(field);
			case field instanceof CollectionModelFieldVerticalSeparation:
				return this.extractVerticalSeparation(field);
			default:
				return this.extractString(field);
		}
	}

	private static extractLocation(field: CollectionModelFieldLocation): string {
		const lat = IkeUnit.round(field.value.latitude, LOCATION_PRECISION);
		const lng = IkeUnit.round(field.value.longitude, LOCATION_PRECISION);

		if (field.value.altitude != null) {
			const alt = IkeUnit.display(field.value.altitude, Services.UserSettings.units);
			return Translate(T.REPORT_COLLECTION_LAT_LNG_ALT, { lat, lng, alt });
		} else {
			return Translate(T.REPORT_COLLECTION_LAT_LNG, { lat, lng });
		}
	}

	private static extractVector(field: CollectionModelFieldVector): string {
		const distance = IkeUnit.display(field.value.distance, Services.UserSettings.units);
		const bearing = IkeUnit.displayAngle(field.value.bearing, IkeUnit.DEGREE);
		return Translate(T.REPORT_COLLECTION_VECTOR, { distance, bearing });
	}

	private static extractAnchor(field: CollectionModelFieldAnchor): string {
		if (CollectionModelFieldAnchor.isCollectionWithVectorTool(field.value)) {
			const distance = IkeUnit.display(field.value.distance, Services.UserSettings.units);
			const bearing = IkeUnit.displayAngle(field.value.bearing, IkeUnit.DEGREE);
			return Translate(T.REPORT_COLLECTION_VECTOR, { distance, bearing });
		}

		if (CollectionModelFieldAnchor.isCollectionWithLocationTool(field.value)) {
			const lat = IkeUnit.round(field.value.latitude, LOCATION_PRECISION);
			const lng = IkeUnit.round(field.value.longitude, LOCATION_PRECISION);

			if (field.value.altitude != null) {
				const alt = IkeUnit.display(field.value.altitude, Services.UserSettings.units);
				return Translate(T.REPORT_COLLECTION_LAT_LNG_ALT, { lat, lng, alt });
			} else {
				return Translate(T.REPORT_COLLECTION_LAT_LNG, { lat, lng });
			}
		}

		throw new Error('Unknown anchor tool');
	}

	private static extractHeight(field: CollectionModelFieldHeight): string {
		return IkeUnit.display(field.value, Services.UserSettings.units);
	}

	private static extractNumber(field: CollectionModelFieldNumber): string {
		if (field.isSag()) {
			return IkeUnit.display(field.value, Services.UserSettings.units);
		}

		if (field.isBearing() || field.isBackBearing()) {
			return IkeUnit.displayAngle(field.value, IkeUnit.DEGREE);
		}

		return field.displayValue;
	}

	private static extractYesNo(field: CollectionModelFieldYesNo): string {
		return Translate(field.value ? T.YES : T.NO);
	}

	private static extractSelectList(field: CollectionModelFieldSelectList): string {
		return field.displayValue;
	}

	private static extractVerticalSeparation(field: CollectionModelFieldVerticalSeparation): string {
		return IkeUnit.display(field.value, IkeUnit.INCH, null, null, 0);
	}

	private static extractString(field: CollectionModelField): string {
		return field.displayValue;
	}
}
